import {
	Box,
	Button,
	Slider,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";

import { clamp } from "lodash-es";
import { AlertCircle } from "lucide-react";
import { type FC, useEffect, useState } from "react";
import { ALLOCATION_MIN, ALLOCATION_STEP } from "web_myplann/Goals/constants";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { useAllocationLimits } from "web_myplann/Goals/Hooks/useAllocationLimits";
import type { GoalPortfolioResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { joinTruthy } from "web_myplann/Shared/Utils/textFormatters";

type Props = {
	open: boolean;
	portfolio: GoalPortfolioResponseDTO | null;
	onClose: () => void;
	onSubmit: (
		portfolioId: number,
		usagePercent: number,
		options?: { onSettled?: () => void },
	) => void;
};

const buildSubtitle = (
	portfolio: GoalPortfolioResponseDTO,
	domainLabel: string | undefined,
): string =>
	joinTruthy([domainLabel, portfolio.producer, portfolio.product], ", ");

export const EditAllocationDrawer: FC<Props> = ({
	open,
	portfolio,
	onClose,
	onSubmit,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	const { currentForThis, usedOnOther, maxForThis } =
		useAllocationLimits(portfolio);

	const [draft, setDraft] = useState(currentForThis);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (open) {
			setDraft(currentForThis);
			setSubmitting(false);
		}
	}, [open, currentForThis]);

	if (!portfolio) return null;

	const clampedDraft = clamp(draft, ALLOCATION_MIN, maxForThis);
	const totalValue = portfolio.portfolioValue ?? 0;
	const previewValue = Math.round((totalValue * clampedDraft) / 100);
	const subtitle = buildSubtitle(
		portfolio,
		portfolio.domain
			? t(Resources.Portfolio.List.PortfolioDomainType[portfolio.domain])
			: undefined,
	);

	const handleSliderChange = (_: Event, value: number | number[]) => {
		setDraft(typeof value === "number" ? value : value[0]);
	};

	const handleSubmit = () => {
		setSubmitting(true);
		onSubmit(portfolio.portfolioId, clampedDraft, {
			onSettled: () => {
				setSubmitting(false);
				onClose();
			},
		});
	};

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(Resources.Goals.Detail.Solution.Actions.editAllocation)}
		>
			<Stack px={1.6} pb={1.6} gap={1.6}>
				{subtitle && (
					<Typography variant="caption" color={theme.palette.grey[500]}>
						{subtitle}
					</Typography>
				)}

				<Stack>
					<Stack
						direction="row"
						alignItems="baseline"
						justifyContent="space-between"
					>
						<Typography variant="body1" color={theme.palette.grey[600]}>
							{t(Resources.Goals.Detail.Solution.Allocation.sliderLabel)}
						</Typography>
						<Typography variant="h1" tabular>
							{clampedDraft} %
						</Typography>
					</Stack>
					<Slider
						variant="flat"
						value={clampedDraft}
						onChange={handleSliderChange}
						min={ALLOCATION_MIN}
						max={maxForThis}
						step={ALLOCATION_STEP}
					/>
					<Stack direction="row" justifyContent="space-between">
						<Typography variant="hint" tabular>
							{ALLOCATION_MIN} %
						</Typography>
						<Typography variant="hint" tabular>
							{maxForThis} %
						</Typography>
					</Stack>
				</Stack>

				{usedOnOther > 0 && (
					<Stack
						direction="row"
						alignItems="flex-start"
						gap={1}
						px={1.2}
						py={1}
						bgcolor={theme.palette.blue[50]}
						borderRadius={1.2}
					>
						<Box mt={0.3} flexShrink={0}>
							<AlertCircle size={14} color={theme.palette.blue[600]} />
						</Box>
						<Typography
							variant="caption"
							color={theme.palette.blue[700]}
							tabular
						>
							{t(Resources.Goals.Detail.Solution.Allocation.usedOnOther, {
								usedPercent: usedOnOther,
								maxPercent: maxForThis,
							})}
						</Typography>
					</Stack>
				)}

				<Stack
					bgcolor={theme.palette.surface.muted}
					borderRadius={1.2}
					px={1.2}
					py={1.2}
					gap={0.4}
				>
					<Typography variant="overline" color={theme.palette.grey[500]}>
						{t(Resources.Goals.Detail.Solution.Allocation.previewTitle)}
					</Typography>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						py={0.4}
					>
						<Typography variant="body1" color={theme.palette.grey[600]}>
							{t(Resources.Goals.Detail.Solution.Allocation.valueLabel)}
						</Typography>
						<Typography variant="bodyStrong" tabular>
							{formatAmount(previewValue)}{" "}
							{t(Resources.Goals.Amount.currencySign)}{" "}
							<Typography variant="hint" component="span">
								{t(Resources.Goals.Detail.Solution.Allocation.outOf, {
									amount: formatAmount(totalValue),
								})}
							</Typography>
						</Typography>
					</Stack>
				</Stack>

				<Button
					variant="primary"
					onClick={handleSubmit}
					disabled={clampedDraft === currentForThis}
					loading={submitting}
				>
					{t(Resources.Goals.Detail.Solution.Allocation.submit)}
				</Button>
			</Stack>
		</MyPlannDrawer>
	);
};
